import React, { useMemo } from 'react'
import { PhoneIcon, ThunderIcon, TrophyIcon, ClientSvg } from '../../assets'
import { colors } from '../../styles/colors'
import './card.scss'
import { useTranslation } from 'react-i18next'
import { numberFormatter, constants, formatPhone } from 'helpers'
import { getGenderAgeProfile } from 'pages/village/helpers/villageClientHelpers'
import { ClientAvatar } from './ClientAvatar'
import { useAnalytics } from 'hooks'

interface CardVillageClientV2Props {
    clientCode: string
    fullname: string
    sex: string
    age: number
    remainingAmount: number
    balance: number
    lastPaymentDate: string
    phoneNumber?: string
    paymentProgress: number
    onClick?: () => void
    className?: string
    duplicate: boolean
    categories?: string[]
}

export const CardVillageClientV2: React.FC<CardVillageClientV2Props> = ({
    clientCode,
    fullname,
    sex,
    age,
    remainingAmount,
    balance,
    lastPaymentDate,
    phoneNumber,
    paymentProgress,
    onClick,
    className,
    categories = [],
    ...props
}) => {
    const { t } = useTranslation()
    const { trackEvent } = useAnalytics()

    const { CerealIcon, PoultryIcon, VegetableIcon } = ClientSvg

    const isPaymentComplete = paymentProgress >= 100
    const phoneIconColor = isPaymentComplete ? colors.purple : colors.primary
    const trophyColor = isPaymentComplete ? colors.purple : colors.mediumGrey
    const lastPaymentFormatted =
        lastPaymentDate &&
        t('dayMonth', {
            date: new Date(lastPaymentDate)
        })
    const paymentText = isPaymentComplete
        ? t('village.finished') + ': ' + numberFormatter(balance)
        : t('village.remaining') + ': ' + numberFormatter(remainingAmount)

    const handlePhoneClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (!phoneNumber) return
        trackEvent({
            category: constants.ANALYTICS.VILLAGE_PAGE,
            action: constants.ANALYTICS.ACTIONS.PHONE_CALL
        })
        window.location.href = `tel:${phoneNumber}`
    }

    const setThunderIconColor = (threshold: number) => {
        if (paymentProgress >= 100) return colors.purple
        if (paymentProgress >= threshold) return colors.yellow
        return colors.mediumGrey
    }

    const genderIconUrl = useMemo(() => {
        return getGenderAgeProfile(t, sex, age)
    }, [sex, age, t])

    return (
        <button
            className={`card-village-client-v2 ${className || ''}`.trim()}
            onClick={onClick}
            aria-label="village-client-card"
            {...props}
        >
            <div className="card-village-client-v2__content">
                <div className="card-village-client-v2__avatar-container">
                    <ClientAvatar
                        genderIconUrl={genderIconUrl}
                        duplicate={props.duplicate}
                        fullname={fullname}
                    />
                </div>
                <div className="card-village-client-v2__info">
                    <div className="card-village-client-v2__header">
                        <div className="card-village-client-v2__id-section">
                            <span className="card-village-client-v2__id">
                                {t('village.id')}: {clientCode}
                            </span>
                            <div className="card-village-client-v2__progress-icons">
                                <ThunderIcon
                                    color={setThunderIconColor(25)}
                                    size={15}
                                />
                                <ThunderIcon
                                    color={setThunderIconColor(50)}
                                    size={15}
                                />
                                <ThunderIcon
                                    color={setThunderIconColor(75)}
                                    size={15}
                                />
                                <TrophyIcon
                                    color={trophyColor}
                                    width="15"
                                    height="15"
                                />
                            </div>
                        </div>
                        <h3 className="card-village-client-v2__name">
                            {fullname}
                        </h3>
                        {phoneNumber ? (
                            <p className="card-village-client-v2__phone">
                                {formatPhone(phoneNumber)}
                            </p>
                        ) : null}
                    </div>
                    <div className="card-village-client-v2__payment-info">
                        {balance || balance === 0 ? (
                            <div>
                                <p
                                    className={`card-village-client-v2__last-payment ${
                                        isPaymentComplete
                                            ? 'card--available-finished-client-balance-color'
                                            : balance < 1
                                            ? ''
                                            : 'card--available-client-balance-color'
                                    }`}
                                >
                                    {`${t(
                                        'village.available'
                                    )}: ${numberFormatter(balance)}`}
                                </p>
                            </div>
                        ) : (
                            ''
                        )}
                        {Boolean(remainingAmount || isPaymentComplete) && (
                            <div
                                className={`card-village-client-v2__${
                                    isPaymentComplete ? 'finished' : 'remaining'
                                }`}
                            >
                                {paymentText}
                            </div>
                        )}
                        {lastPaymentFormatted && (
                            <div className="card-village-client-v2__last-payment">
                                {t('village.last')}: {lastPaymentFormatted}
                            </div>
                        )}
                    </div>
                    <div>
                        {categories.length > 0 && (
                            <div className="card-village-client-v2__categories">
                                {categories.map((category) => (
                                    <span
                                        key={category}
                                        className={`card-village-client-v2__category card-village-client-v2__category--${category.toLowerCase()}`}
                                    >
                                        {category === 'Poultry' && (
                                            <PoultryIcon
                                                width="16"
                                                height="18"
                                                color={colors.lighterBlack}
                                                aria-label="poultry-icon"
                                            />
                                        )}
                                        {category === 'Cereal' && (
                                            <CerealIcon
                                                width="24"
                                                height="24"
                                                color={colors.lighterBlack}
                                                aria-label="cereal-icon"
                                            />
                                        )}
                                        {category === 'Vegetable' && (
                                            <VegetableIcon
                                                width="24"
                                                height="24"
                                                color={colors.lighterBlack}
                                                aria-label="vegeatable-icon"
                                            />
                                        )}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <button
                className="card-village-client-v2__action"
                onClick={handlePhoneClick}
            >
                {PhoneIcon({
                    color: phoneIconColor,
                    fillColor: phoneIconColor,
                    width: '28',
                    height: '28'
                })}
            </button>
        </button>
    )
}
